import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { format } from "date-fns";

import Api from "src/Config";

export const AssigneesTransactionContext = createContext(null);

type Props = {
  children: ReactElement;
};

const AssigneesTransactionProvider: FunctionComponent<Props> = ({
  children,
}) => {
  const { id } = useParams();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["assignees_transaction", id],
    staleTime: 30000,
    queryFn: async () => {
      const { data } = await Api.get(`tm/transactions/assigned/members/${id}`);

      const response = data.results.map(
        (res: {
          id: string;
          user: {
            first_name: string;
            last_name: string;
          };
          assignment_date: string;
        }) => ({
          id: res.id,
          "transactions assigned":
            `${res.user.first_name || "N/A"} ${res.user.last_name || ""}` ||
            "N/A",
          "date and time of assignment": format(
            new Date(res.assignment_date),
            "dd MMM yyyy, hh:mm a",
          ),
        }),
      );

      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,
    }),
    [isLoading, data, error, isFetching, pageCount, pageNumber, setPageNumber],
  );
  return (
    <AssigneesTransactionContext.Provider value={contextValue}>
      {children}
    </AssigneesTransactionContext.Provider>
  );
};

export default AssigneesTransactionProvider;
